import React, { useState, ChangeEvent } from 'react';
import { TextField } from '@cimpress/react-components';

interface TraceRulerSettingsProps {
    color: string;
    spacing: number;
    setTraceRulerColor: (color: string) => void;
    setTraceRulerSpacing: (spacing: number) => void;
}

const isError = (value: number) => (!(Number.isFinite(value) && value >= 10 && value <= 500) ? 'error' : undefined);

const TraceRulerSettings: React.FC<TraceRulerSettingsProps> = ({
    color,
    spacing,
    setTraceRulerColor,
    setTraceRulerSpacing,
}) => {
    // Tracks the spacing last confirmed to have come from `spacing` itself, kept separate from
    // `currentSpacing` so an in-progress (possibly invalid) edit never gets stomped by this sync
    // just because it doesn't yet match the prop -- the same pattern used by NumberInput/Segments/
    // CustomTick/PrecisionWrap, which traceRulerSettings was previously missing. Without it, typing
    // an out-of-range value got reverted to the last valid prop value before ever being painted,
    // because this component re-renders on every keystroke and this sync ran on every one of those
    // renders too, each time seeing `spacing !== currentSpacing` and overwriting the fresh edit.
    const [validSpacing, setValidSpacing] = useState(spacing);
    const [currentSpacing, setCurrentSpacing] = useState(spacing);

    if (spacing !== validSpacing) {
        setValidSpacing(spacing);
        setCurrentSpacing(spacing);
    }

    const status = isError(currentSpacing);

    const onColorChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTraceRulerColor(e.target.value);
    };

    const onSpacingChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        const error = isError(value);
        setCurrentSpacing(value);
        if (!error) {
            setTraceRulerSpacing(value);
        }
    };

    return (
        <div className='trace-ruler-settings-panel'>
            <div className='trace-ruler-settings-panel__header'>Crosshair Settings</div>
            <div className='trace-ruler-settings-panel__field'>
                <label htmlFor='trace-ruler-color-input'>Line Color:</label>
                <div className='trace-ruler-settings-panel__color-row'>
                    <input
                        id='trace-ruler-color-input'
                        type='color'
                        value={color}
                        onChange={onColorChange}
                        className='trace-ruler-settings-panel__color-picker'
                    />
                    <span className='trace-ruler-settings-panel__color-value'>{color.toUpperCase()}</span>
                </div>
            </div>
            <div className='trace-ruler-settings-panel__field'>
                <TextField
                    className='trace-ruler-settings-panel__spacing-field'
                    label='Grid Spacing (px)'
                    value={currentSpacing || (currentSpacing === 0 ? 0 : '')}
                    status={status}
                    onChange={onSpacingChange}
                    type='number'
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                />
            </div>
        </div>
    );
};

export default React.memo(TraceRulerSettings);
